import { PaymentFrequencyField } from '@domains/subscription/priv/Billables/usecases/CreatePriceForm/PaymentFrequencyField'
import type { BasePrice } from '@domains/subscription/priv/components/Confirmable/confirmable'
import type { PaymentFrequencyRequest } from '@domains/subscription/priv/types'
import { Dialog, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import { Controller, useForm, useWatch } from 'react-hook-form'
import type { CreatePriceFormProps, CreatePricePlugin } from './plugin'

interface PriceForm {
  type: string | undefined
  paymentFrequency: PaymentFrequencyRequest
}

export const CreatePriceForm = (props: CreatePriceFormProps) => {
  const pricePluginNames = props.plugins.map((plugin) => plugin.type)

  const form = useForm<PriceForm>({
    values: {
      type: props.price?.type ?? pricePluginNames[0],
      paymentFrequency: props.price?.paymentFrequency ?? { type: 'anchorable', period: 'monthly' },
    },
  })

  const selectedPriceType = useWatch({ control: form.control, name: 'type' })

  const pricePlugin = props.plugins.find((plugin) => selectedPriceType && plugin.supports(selectedPriceType))

  const headingText = props.mode === 'create' ? 'Create price' : 'Edit price'

  const paymentFrequency = useWatch({ control: form.control, name: 'paymentFrequency' })

  if (isUnsupportedPrice(pricePlugin, props.price)) {
    return (
      <Dialog open={props.open} onClose={props.onClose}>
        <Stack
          sx={{
            flex: 1,
            gap: 3,
            padding: 2,
            minWidth: 400,
          }}
        >
          <Stack
            sx={{
              gap: 0.5,
            }}
          >
            <Typography component="h1" variant="p2Medium">
              {headingText}
            </Typography>
            <Typography data-testid="unsupported-price" variant="p4Regular" color="error">
              This billable has a price that is not supported by backoffice
            </Typography>
          </Stack>
        </Stack>
      </Dialog>
    )
  }

  const priceWithControlledPaymentFrequency: BasePrice | undefined = props.price
    ? {
        ...props.price,
        paymentFrequency: paymentFrequency,
      }
    : undefined

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <Stack
        sx={{
          flex: 1,
          gap: 3,
          padding: 2,
          paddingTop: 3,
          minWidth: 400,
        }}
      >
        <Typography variant="h2">{headingText}</Typography>
        <FormControl fullWidth>
          <InputLabel>Price type</InputLabel>
          <Controller
            control={form.control}
            name="type"
            render={({ field }) => (
              <Select
                slotProps={{ input: { 'aria-label': 'price type' } }}
                label="Price type"
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
              >
                {pricePluginNames.map((pluginName) => (
                  <MenuItem key={pluginName} value={pluginName}>
                    {pluginName}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
        <Controller
          control={form.control}
          name="paymentFrequency"
          render={({ field }) => (
            <PaymentFrequencyField
              paymentFrequency={field.value}
              onChange={(data) => {
                field.onChange(data)
              }}
            />
          )}
        />
        {pricePlugin && (
          <pricePlugin.Component
            templateId={props.templateId}
            billableId={props.billableId}
            price={priceWithControlledPaymentFrequency}
            onCreatedPrice={props.onCreatedPrice}
            currencies={props.currencies}
            mode={props.mode}
            additionalPlugins={pricePlugin.additionalPlugins}
            unit={props.unit}
          />
        )}
      </Stack>
    </Dialog>
  )
}

export interface CreateCustomerDiscountPluginComponentProps {
  productId: string
  currency: string
  customerId: string
  onCreatedDiscount: () => void
}

const isUnsupportedPrice = (plugin: CreatePricePlugin | undefined, price: BasePrice | undefined) => {
  return plugin === undefined && price !== undefined
}
